import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Bar,
    Line,
    Scatter,
} from 'recharts';
import { DataKey } from 'recharts/types/util/types';
import { formatNumber } from '../produksi';
import { RawDatum } from './KernelTrend';
import { CustomTooltipDaily } from '../CustomTooltip';

export interface KernelHarianDatum extends RawDatum {
    date: string;
}

interface Props {
    data: KernelHarianDatum[];
}

const COLORS = {
    Produksi: '#82CA9D',
    KER: '#8884D8',
    Stok: '#CA6060',
    Black: '#000000',
    Kirim: '#FFC658',
    jamOlah: '#0088FE',
};

const KernelTrendHarian: React.FC<Props> = ({ data }) => {
    const [hoveringDataKey, setHoveringDataKey] = React.useState<DataKey<any> | undefined>(undefined);

    let stokOpacity = 1;
    let produksiOpacity = 1;
    let kirimOpacity = 1;
    let kerOpacity = 1;

    if (hoveringDataKey === 'Stok') {
        produksiOpacity = 0.5;
        kirimOpacity = 0.5;
        kerOpacity = 0.5;
    } else if (hoveringDataKey === 'Produksi') {
        stokOpacity = 0.5;
        kirimOpacity = 0.5;
        kerOpacity = 0.5;
    } else if (hoveringDataKey === 'Kirim') {
        stokOpacity = 0.5;
        produksiOpacity = 0.5;
        kerOpacity = 0.5;
    } else if (hoveringDataKey === 'KER') {
        stokOpacity = 0.5;
        produksiOpacity = 0.5;
        kirimOpacity = 0.5;
    }

    const handleMouseEnter = (payload: any) => {
        setHoveringDataKey(payload.dataKey);
    };

    const handleMouseLeave = () => {
        setHoveringDataKey(undefined);
    };
    const formattedData = useMemo(() => {
        // pivot raw API → wide format per date
        const tmp: Record<string, any> = {};
        data.forEach(({ date, descr, qty }) => {
            if (!tmp[date]) {
                tmp[date] = { date, Stok: 0, Produksi: 0, Kirim: 0, KER: 0 };
            }
            switch (descr) {
                case 'Stok':
                    tmp[date].Stok = qty/1000;
                    break;
                case 'Produksi':
                    tmp[date].Produksi = qty/1000;
                    break;
                case 'Kirim':
                    tmp[date].Kirim = qty/1000;
                    break;
                case '% KER':
                    tmp[date].KER = qty;
                    break;
                case 'Jam Olah':
                    tmp[date].jamOlah = qty;
                    break;
            }
        });
        return Object.values(tmp).sort(
            (a, b) => parseInt(a.date, 10) - parseInt(b.date, 10)
        );
    }, [data]);

    // fallback ke bulan/tahun sekarang jika data kosong
    const now = new Date();
    const defaultYear = now.getFullYear().toString();
    const defaultMonthNum = String(now.getMonth() + 1).padStart(2, '0');

    const rawPeriod = data[0]?.periode;
    const year = rawPeriod ? rawPeriod.slice(0, 4) : defaultYear;
    const monthNum = rawPeriod ? rawPeriod.slice(4) : defaultMonthNum;

    const monthNames = [
        'Januari','Februari','Maret','April','Mei','Juni',
        'Juli','Agustus','September','Oktober','November','Desember'
    ];
    const monthName = monthNames[parseInt(monthNum, 10) - 1];

    return (
        <Box p={4} display="flex" flexDirection="column" boxShadow="md" height="100%" overflowX="auto" overflowY="hidden">
            <Text fontSize="xl" fontWeight="semibold" mb={4} color="gray.700">
                Produksi Kernel Harian ({monthName} {year})
            </Text>
            <Box
                // flex={1} 
                minH="130px" 
                height="350px"
                minWidth="700px"
                overflowX="auto" 
                overflowY="hidden"
                w="100%"
            >
                <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={formattedData} barGap={0}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis
                            dataKey="date"
                            interval={0}
                            height={80}
                            tick={{ fontSize: 12 }}
                        />

                        {/* YAxis kiri untuk ton */}
                        <YAxis
                            yAxisId="left"
                            tickFormatter={v => formatNumber(Number(v), 'number')}
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Ton', angle: -90, position: 'insideLeft', offset: 2 }}
                            width={60}
                        />

                        {/* YAxis kanan untuk jam & persen */}
                        <YAxis
                            yAxisId="right"
                            tick={{ fontSize: 12 }}
                            orientation="right"
                            tickFormatter={v =>
                                typeof v === 'number'
                                    ? `${v}${v <= 24 ? '' : ''}`
                                    : v
                            }
                            label={{
                                value: 'Jam / %',
                                angle: 90,
                                position: 'insideRight',
                                offset: 20
                            }}
                            // width={80}
                        />

                        <Tooltip
                            content={<CustomTooltipDaily />}
                            formatter={(val, name) =>
                                name === 'jamOlah'
                                    ? `${val} Jam`
                                    : name === 'KER'
                                    ? `${(val as number).toFixed(2)}%`
                                    : formatNumber(Number(val), 'number')
                            }
                            wrapperStyle={{ zIndex: 1000 }}
                        />
                        <Legend
                            verticalAlign="bottom"
                            align="center"
                            height={30}
                            wrapperStyle={{ bottom: 60 }}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        />

                        {/* Bar untuk Stok */}
                        {/* <Bar yAxisId="left" name="Stok" dataKey="Stok" stackId="a" fill={COLORS.Stok} fillOpacity={stokOpacity} /> */}
                        <Bar yAxisId="left" name="Produksi" dataKey="Produksi" stackId="a" fill={COLORS.Produksi} fillOpacity={produksiOpacity} />
                        <Bar yAxisId="left" name="Kirim" dataKey="Kirim" fill={COLORS.Kirim} fillOpacity={kirimOpacity} />

                        {/* Line untuk % KER */}
                        <Line
                            yAxisId="right"
                            name="KER"
                            type="linear"
                            dataKey="KER"
                            stroke={COLORS.KER}
                            strokeWidth={2}
                            strokeOpacity={kerOpacity}
                            dot={{ r: 7 }}
                            activeDot={{ r: 7 }}
                        />
                        <Scatter
                            yAxisId="right"
                            name="Jam Olah"
                            dataKey="jamOlah"
                            fill={COLORS.Black}
                            shape="cross"
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default KernelTrendHarian;